import { useEffect, useState } from 'react'
import { formatCountdown } from '@/lib/flight-utils'

interface CountdownRingTextTokens {
  primary: string
  subtle: string
  loading: string
}

interface CountdownRingProps {
  /** Departure time to count down to. */
  targetDate: Date
  /** Force the drained "DEPARTED" state (e.g. flight already in progress). */
  departed?: boolean
  /** Variant text colour tokens, reused from FlightCard. */
  text: CountdownRingTextTokens
  /** Size of the ring window in hours (arc is full at/over this). */
  windowHours?: number
  /** Rendered diameter in px; the SVG scales via viewBox. */
  size?: number
}

const SIZE = 88
const R = 41.4
const STROKE = 4.7
const CIRCUMFERENCE = 2 * Math.PI * R

export function CountdownRing({
  targetDate,
  departed = false,
  text,
  windowHours = 24,
  size = SIZE,
}: CountdownRingProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const msRemaining = targetDate.getTime() - now
  const isDeparted = departed || msRemaining <= 0

  const progress = isDeparted
    ? 0
    : Math.min(1, Math.max(0, msRemaining / (windowHours * 3_600_000)))

  const label = isDeparted ? 'Departed' : 'Departs in'
  const value = isDeparted ? '' : formatCountdown(msRemaining)
  // The SVG scales via viewBox, but the HTML text overlay doesn't — tighten it below 80px.
  const compact = size < 80

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        className="absolute inset-0"
        width={size}
        height={size}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        fill="none"
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          strokeWidth={STROKE}
          className="stroke-white/90"
        />
        {progress > 0 && (
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            className="[stroke:var(--content-brand-bold)] transition-[stroke-dashoffset] duration-500"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {value && (
          <span className={`${compact ? 'text-sm leading-5' : 'text-base leading-6'} font-semibold ${text.subtle} tracking-[-0.5px]`}>
            {value}
          </span>
        )}
        <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} font-semibold uppercase ${text.subtle} leading-4`}>
          {label}
        </span>
      </div>
    </div>
  )
}
